import { useEffect, useRef, useState } from 'react';

interface ScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  delay?: number;
}

export const useSimpleScrollReveal = (options: ScrollRevealOptions = {}) => {
  const ref = useRef<HTMLElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    const {
      threshold = 0.05,
      rootMargin = '-50px',
      delay = 0
    } = options;
    
    // Add initial hidden styles
    element.style.opacity = '0';
    element.style.transform = 'translateY(20px)';
    element.style.transition = `opacity 0.8s ease-out ${delay}ms, transform 0.8s ease-out ${delay}ms`;
    
    let timeoutId: NodeJS.Timeout;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            // Use timeout to ensure the delay works properly
            timeoutId = setTimeout(() => {
              if (element) {
                element.style.opacity = '1';
                element.style.transform = 'translateY(0)';
                setHasAnimated(true);
              }
            }, 50); // Small delay to ensure styles are applied
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );
    
    observer.observe(element);
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []); // Remove dependencies to prevent re-initialization
  
  return ref;
};

export const useParallax = () => {
  const ref = useRef<HTMLElement>(null);
  
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const rate = scrolled * -0.5;
      element.style.transform = `translateY(${rate * 0.1}px)`;
    };
    
    // Initial position
    handleScroll();
    
    // Throttle scroll events
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', scrollHandler, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, []);
  
  return ref;
};